import React from 'react';
import { Category, Country, Language, PodcastMetadata } from '../../types';
import { CategoryForm, CategoryFormData, PlaceholderCategory } from './CategoryForm';
import { LanguageField } from './FormFields/LanguageField';
import { CountryField } from './FormFields/CountryField';
import get from 'lodash/get';
import produce from 'immer';
import { Type } from '@spotify-internal/encore-creator-web';
import { PodcastModalBody } from '../Shared/PodcastModal';

type FormData = Partial<PodcastMetadata>;

interface Props {
  countries: Country[];
  languages: Language[];
  categories: Category[];
  metadata: null | PodcastMetadata;
  onSuccessfulValidation(metadata: PodcastMetadata): void;
}

export function AddMetadataContainer(props: Props) {
  const [metadata, setMetadata] = React.useState<FormData>(props.metadata ? props.metadata : {});

  function isValid(data: FormData) {
    /**
     * if all 3 form fields are present and the `categories` field has at least one
     * primary category selected.
     */
    if (data.countryOfOrigin && data.language && get(data, 'categories', []).length > 0) {
      return true;
    }
    return false;
  }

  function handleOnChange(key: string) {
    return (newMetadata: Country | Language | CategoryFormData) => {
      const nextState: FormData = {
        ...metadata,
        [key]: newMetadata,
      };
      setMetadata(nextState);
      if (isValid(nextState)) {
        props.onSuccessfulValidation(
          produce(nextState, draft => {
            draft.categories = draft.categories!.filter(
              cat => !(cat instanceof PlaceholderCategory),
            );
          }) as PodcastMetadata,
        );
      }
    };
  }

  return (
    <PodcastModalBody data-testid="metadata-screen">
      <Type.h1 variant={Type.heading2}>Tell us more about your podcast</Type.h1>
      <Type.p variant={Type.body1}>
        This will help more podcast listeners on Spotify discover your episodes.
      </Type.p>
      <div
        style={{
          maxWidth: '456px',
        }}
      >
        <CountryField
          countries={props.countries}
          value={metadata.countryOfOrigin ? metadata.countryOfOrigin.code : ''}
          onChange={handleOnChange('countryOfOrigin')}
        />
        <LanguageField
          languages={props.languages}
          value={metadata.language ? metadata.language.code : ''}
          onChange={handleOnChange('language')}
        />
        <CategoryForm
          categories={props.categories}
          value={metadata.categories ? metadata.categories : []}
          onChange={handleOnChange('categories')}
        />
      </div>
    </PodcastModalBody>
  );
}
